import React, { createContext, useContext, useState, ReactNode } from "react";
import { AppContext } from "@lib/types/Context";

interface SharedContextType {
  // Current context
  currentContext: AppContext | null;
  setCurrentContext: (context: AppContext | null) => void;

  // Common data
  currentUserId: string | null;
  setCurrentUserId: (userId: string | null) => void;

  // Common permissions
  commonPermissions: {
    canViewDashboard: boolean;
    canViewSettings: boolean;
    canViewNotifications: boolean;
    canViewProfile: boolean;
  };

  // Common UI state
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;

  // Common methods
  refreshCommonData: () => Promise<void>;
  updateCommonPermissions: (
    permissions: Partial<SharedContextType["commonPermissions"]>,
  ) => void;
  switchContext: (context: AppContext, id?: string) => Promise<void>;
}

const SharedContext = createContext<SharedContextType | undefined>(undefined);

interface SharedProviderProps {
  children: ReactNode;
}

export const SharedProvider: React.FC<SharedProviderProps> = ({ children }) => {
  const [currentContext, setCurrentContext] = useState<AppContext | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const defaultCommonPermissions = {
    canViewDashboard: true,
    canViewSettings: true,
    canViewNotifications: true,
    canViewProfile: true,
  };

  const refreshCommonData = async () => {
    setIsLoading(true);
    try {
      // API call to fetch common data
      // const response = await fetchCommonData();
      // setCommonData(response.data);
    } catch (error) {
      console.error("Error fetching common data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateCommonPermissions = (
    _newPermissions: Partial<SharedContextType["commonPermissions"]>,
  ) => {
    // Update common permissions logic
  };

  const switchContext = async (context: AppContext, _id?: string) => {
    setIsLoading(true);
    try {
      // Context switching logic
      setCurrentContext(context);

      // Load context-specific data
      switch (context) {
        case AppContext.CUSTOMER:
          // Load white label specific data
          break;
        case AppContext.CUSTOMER_LEAD:
          // Load white label lead specific data
          break;
        case AppContext.PAYMENT_PROVIDER:
          // Load payment provider specific data
          break;
      }
    } catch (error) {
      console.error("Error switching context:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const value: SharedContextType = {
    currentContext,
    setCurrentContext,
    currentUserId,
    setCurrentUserId,
    commonPermissions: defaultCommonPermissions,
    isLoading,
    setIsLoading,
    refreshCommonData,
    updateCommonPermissions,
    switchContext,
  };

  return (
    <SharedContext.Provider value={value}>{children}</SharedContext.Provider>
  );
};

export const useShared = (): SharedContextType => {
  const context = useContext(SharedContext);
  if (context === undefined) {
    throw new Error("useShared must be used within a SharedProvider");
  }
  return context;
};
